import React from 'react';
import { useBranding } from '@/contexts/shared/BrandingContext';
import { useTheme } from '@/contexts/shared/ThemeContext';

import DefaultLogoSvg from '../../../../public/assets/logos/logo-white.svg?react';
import DefaultLogoLightSvg from '../../../../public/assets/logos/logo-full.svg?react';
import DefaultLogoMarkSvg from '../../../../public/assets/logos/logo-icon.svg?react';

import { tr } from '@/i18n';
interface BrandLogoProps {
  className?: string;
  /** 'full' = wordmark (default); 'mark' = compact square for the collapsed rail. */
  variant?: 'full' | 'mark';
}

/** Tenant logo with theme awareness — light logo in light mode when set,
 *  falling back to the default iLearn wordmark (or square mark). */
export const BrandLogo: React.FC<BrandLogoProps> = ({ className, variant = 'full' }) => {
  const { logoUrl, logoLightUrl, logoMarkUrl, logoMarkLightUrl } = useBranding();
  const { theme } = useTheme();

  const activeLogo = theme === 'light' && logoLightUrl ? logoLightUrl : logoUrl;

  if (variant === 'mark') {
    // Dedicated square mark from tenant settings (theme-aware, either one
    // falls back to the other).
    const activeMark = theme === 'light'
      ? (logoMarkLightUrl || logoMarkUrl)
      : (logoMarkUrl || logoMarkLightUrl);
    if (activeMark) {
      return (
        <img
          src={activeMark}
          alt={tr("ui.company_logo", "Company Logo")}
          className={className}
          style={{ objectFit: 'contain' }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      );
    }
    // No mark uploaded: crop the tenant wordmark via the caller's CSS
    // (object-fit: cover + object-position: left)…
    if (activeLogo) {
      return (
        <img
          src={activeLogo}
          alt={tr("ui.company_logo", "Company Logo")}
          className={className}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      );
    }
    // …or use the default iLearn square mark.
    return <DefaultLogoMarkSvg className={className} aria-label={tr("ui.company_logo", "Company Logo")} />;
  }

  if (activeLogo) {
    return (
      <img
        src={activeLogo}
        alt={tr("ui.company_logo", "Company Logo")}
        className={className}
        style={{ objectFit: 'contain' }}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    );
  }
  if (theme === 'light') {
    return <DefaultLogoLightSvg className={className} aria-label={tr("ui.company_logo", "Company Logo")} />;
  }
  return <DefaultLogoSvg className={className} aria-label={tr("ui.company_logo", "Company Logo")} />;
};
